import React, { useState, useEffect, useCallback, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import styled from "styled-components"
import { FRONTEND_LINKS } from "../../../../Constants/FrontendLinks"
import { useEndpointProvider } from "../../../../Providers/EndpointProvider"
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider"
import { Deployment, useEnvironmentVariablesProvider } from "../../../../Providers/EnvironmentVariablesProvider"
import SubpageTabs from "../../../../Components/SubpageTabs/SubpageTabs"
import { SubHeader } from "./Components/SubHeader"
import ExportsListView from "../Export/ExportsListView"
import ExportsStorageMeter from "../Export/ExportsStorageMeter"


// 5s cadence is intentional (chunk 5.0 brief) — no Socket.IO on this tab, so we
// poll on a faster cycle than the global Tracker modal (which uses Socket.IO + 30s
// fallback). Polling pauses when the document is hidden to avoid background traffic.
const POLL_INTERVAL_MS = 5000


const RefreshControls = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    color: var(--text-secondary-color);
    font-size: 12px;
    margin-bottom: 8px;
`


const PatientExports = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const endpointProvider = useEndpointProvider()
    const { LINKS } = useBackendLinksProvider()
    const { deployment, environmentVariables } = useEnvironmentVariablesProvider()
    const isClinical = deployment === Deployment.CLINICAL
    const exportFeatureEnabled = isClinical && !!environmentVariables?.FEATURE_FLAG_EXPORT

    const query = new URLSearchParams(location.search)
    const UID = parseInt(query.get("patient_id"), 10)
    const [patient, setPatient] = useState(location?.state)

    // Export is clinical-only AND behind FEATURE_FLAG_EXPORT. If someone hits
    // this URL directly when it's not available, bounce them to the Data tab.
    useEffect(() => {
        if (!exportFeatureEnabled && UID) {
            navigate(
                `${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.SUBPAGES.PATIENTS.MAIN}?patient_id=${UID}`,
                { replace: true, state: location?.state }
            )
        }
    }, [exportFeatureEnabled, UID, navigate, location?.state])

    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [stats, setStats] = useState(null)   // { job_count, completed_count, total_bytes }
    const isMountedRef = useRef(true)

    // Hydrate patient if we landed here without state (e.g. direct URL navigation).
    // Matches the pattern in VisualizePatient.js. Skipped when the feature is
    // off — the bounce effect above already redirects in that case.
    useEffect(() => {
        if (!exportFeatureEnabled) return
        if (Number.isNaN(UID)) {
            navigate(FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN, { replace: true })
            return
        }
        if (!patient || Object.keys(patient).length === 0) {
            endpointProvider
                .post(LINKS.DATA.PROFILING.GET_PATIENT, { patient_primary_key: UID })
                .then(p => { if (isMountedRef.current) setPatient(p) })
                .catch(() => {
                    if (isMountedRef.current) setError(`Patient id=${UID} does not exist.`)
                })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [UID, exportFeatureEnabled])

    const fetchStats = useCallback(async () => {
        if (Number.isNaN(UID)) return
        try {
            const response = await endpointProvider.get(
                LINKS.DATA.EXPORTS.STATS,
                { patient_id: UID }
            )
            if (!isMountedRef.current) return
            setStats(response || null)
        } catch {
            // Stats are a polish-layer detail; swallow errors.
        }
    }, [UID, endpointProvider, LINKS])

    const fetchExports = useCallback(async () => {
        if (Number.isNaN(UID)) return
        try {
            const response = await endpointProvider.get(
                LINKS.DATA.EXPORTS.LIST,
                { patient_id: UID, page: 1, page_size: 200 }
            )
            if (!isMountedRef.current) return
            const fetched = response?.items || []
            // Merge with existing rows so live IN_PROGRESS progress isn't wiped
            // by a poll cycle that lands between Socket.IO updates. (This tab
            // doesn't subscribe to Socket.IO, but the merge is symmetric with
            // the modal and harmless when nothing is in flight.)
            setItems(prev => {
                const prevById = new Map(prev.map(j => [j.id, j]))
                return fetched.map(serverItem => {
                    const existing = prevById.get(serverItem.id)
                    const terminal = serverItem.status === "COMPLETED"
                        || serverItem.status === "FAILED"
                        || serverItem.status === "CANCELLED"
                        || serverItem.status === "DELETED"
                    if (existing && !terminal) {
                        return { ...serverItem, ...existing }
                    }
                    return serverItem
                })
            })
            setError(null)
        } catch (err) {
            if (!isMountedRef.current) return
            setError(err?.message || "Failed to load exports.")
        } finally {
            if (isMountedRef.current) setLoading(false)
        }
    }, [UID, endpointProvider, LINKS])

    // Initial fetch on mount.
    useEffect(() => {
        isMountedRef.current = true
        fetchExports()
        fetchStats()
        return () => {
            isMountedRef.current = false
        }
    }, [fetchExports, fetchStats])

    // Polling lifecycle — pause while the document is hidden so we don't burn
    // bandwidth on a background tab. Resume + immediate refresh on return.
    useEffect(() => {
        let intervalId = null

        const startPolling = () => {
            if (intervalId !== null) return
            intervalId = setInterval(() => {
                fetchExports()
                fetchStats()
            }, POLL_INTERVAL_MS)
        }
        const stopPolling = () => {
            if (intervalId !== null) {
                clearInterval(intervalId)
                intervalId = null
            }
        }
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                fetchExports()
                fetchStats()
                startPolling()
            } else {
                stopPolling()
            }
        }

        if (document.visibilityState === "visible") {
            startPolling()
        }
        document.addEventListener("visibilitychange", handleVisibilityChange)

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange)
            stopPolling()
        }
    }, [fetchExports, fetchStats])

    // Redirected by the effect above when export isn't available; render
    // nothing in the interim so there's no flash of export UI.
    if (!exportFeatureEnabled) return null

    return (
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                padding: "6px 0px",
                marginTop: "8px",
                marginBottom: "16px",
                marginLeft: "32px",
                marginRight: "32px",
                borderBottom: "1px solid var(--color-border-default)",
                paddingBottom: "12px",
            }}>
                <SubHeader patient={patient} />
                <SubpageTabs UID={UID} patient={patient} currentTab={"Exports"} />
            </div>

            <div style={{
                padding: "0 32px",
                backgroundColor: "var(--color-surface-primary)",
                height: "calc(100vh - 200px)",
                minHeight: "500px",
                maxHeight: "80vh",
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
            }}>
                {error && (
                    <div style={{ color: "#d32f2f", marginBottom: 12 }}>{error}</div>
                )}

                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                }}>
                    <RefreshControls>
                        <ExportsStorageMeter stats={stats} />
                    </RefreshControls>
                </div>

                <div style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}>
                    <ExportsListView
                        items={items}
                        setItems={setItems}
                        onRefresh={() => { fetchExports(); fetchStats(); }}
                        loading={loading}
                        emptyState={
                            <div style={{
                                textAlign: "center",
                                padding: 40,
                                color: "var(--text-secondary-color)",
                                fontStyle: "italic",
                            }}>
                                No exports for this patient yet. Start one from the Data tab
                                using the Actions menu, or from the Data Visualize page.
                            </div>
                        }
                    />
                </div>
            </div>
        </div>
    )
}

export default PatientExports
